import { PlanType } from '@mode-switch/query';
import { Flamingo } from '@mode/charts/util-vegas';
import { fromReportViewerSelectors } from '@mode/report-viewer/domain';
import { EmbedReportState } from '@mode/reports/sandbox/contract';
import { AppState } from '@mode/legacy/types-app-state';
import { ChartTypes, FlamingoTypes, RMap } from '@mode/shared/contract-common';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { fromSwitchViewSelectors } from '@mode/switch-views/domain';
import { fromPlan } from '@mode/switch-views/domain';
import { createSelector, createStructuredSelector } from 'reselect';
import { ReportLayoutSelectors } from '../report-layout.selectors';

/**
 * This is for building a serializable state for use in the iframe. Everything
 * returned by the embedState(Gf) selector will be serialized and synchronized
 * to the iframe. Then the embed-report.selectors can be used to pick out the
 * state from there.
 *
 * Since this file runs in the main app it can pull state from any module that
 * is necessary.
 */

function flamingoResultsProjection(
  refs: FlamingoTypes.FlamingoPlanRef,
  plans: fromPlan.State,
  tablePaging: RMap<ChartTypes.ChartPaging>,
  tableColumnSearch: RMap<ChartTypes.TableColumnSearch>,
  chartTokens: string[]
) {
  return chartTokens
    .filter((token) => refs[token] != null && plans.requests[refs[token]].type === PlanType.Records)
    .reduce((data, token) => {
      const result = plans.results[refs[token]];
      const request = plans.requests[refs[token]];

      if (request && result) {
        const count = Flamingo.getCountValue(request, result);
        const recordsResult = Flamingo.getRecordsInline(request, result);
        data[token] = {
          content: recordsResult.items,
          count: count,
          ...tablePaging[token],
          ...tableColumnSearch[token],
        };
      }

      return data;
    }, {} as { [token: string]: EmbedReportState.FlamingoRecordResults });
}

export const flamingoResults = createSelector(
  (state: AppState) => state.flamingo.planRefs,
  (state: AppState) => state.plan,
  (state: AppState) => state.chart.tablePaging,
  (state: AppState) => state.chart.tableColumnSearch,
  fromReportViewerSelectors.chartTokensInLayoutGf,
  (refs, plans, tablePaging, tableColumnSearch, chartTokens) => {
    return flamingoResultsProjection(refs, plans, tablePaging, tableColumnSearch, chartTokens);
  }
);

// The Builder is not yet migrated to the new state, so we need
// to use the legacy chartTokensInLayout selector in the interim.
export const flamingoResultsForBuilder = createSelector(
  (state: AppState) => state.flamingo.planRefs,
  (state: AppState) => state.plan,
  (state: AppState) => state.chart.tablePaging,
  (state: AppState) => state.chart.tableColumnSearch,
  ReportLayoutSelectors.chartTokensInLayout,
  (refs, plans, tablePaging, tableColumnSearch, chartTokens) => {
    return flamingoResultsProjection(refs, plans, tablePaging, tableColumnSearch, chartTokens);
  }
);

export const pivotResults = createSelector(
  (state: AppState) => state.flamingo.planRefs,
  (state: AppState) => state.plan,
  ReportLayoutSelectors.chartTokensInLayout,
  (refs, plans, chartTokens) =>
    chartTokens
      .filter(
        (token) =>
          refs[token] != null &&
          (plans.requests[refs[token]].type === PlanType.PivotDetail ||
            plans.requests[refs[token]].type === PlanType.PivotBasic)
      )
      .reduce((data, token) => {
        const result = plans.results[refs[token]];
        const request = plans.requests[refs[token]];

        if (request && result) {
          data[token] = {
            plan: request,
            result,
          };
        }

        return data;
      }, {} as { [token: string]: EmbedReportState.PivotInfo })
);

// GF version
export const pivotResultsGf = createSelector(
  (state: AppState) => state.flamingo.planRefs,
  (state: AppState) => state.plan,
  fromReportViewerSelectors.chartTokensInLayoutGf,
  (refs, plans, chartTokens) =>
    chartTokens
      .filter(
        (token) =>
          refs[token] != null &&
          (plans.requests[refs[token]].type === PlanType.PivotDetail ||
            plans.requests[refs[token]].type === PlanType.PivotBasic)
      )
      .reduce((data, token) => {
        const result = plans.results[refs[token]];
        const request = plans.requests[refs[token]];

        if (request && result) {
          data[token] = {
            plan: request,
            result,
          };
        }

        return data;
      }, {} as { [token: string]: EmbedReportState.PivotInfo })
);

export const flamingoViews = createSelector(fromSwitchViewSelectors.getModel, (model) =>
  model.views.reduce((acc, v) => {
    acc[v.name] = v;
    return acc;
  }, {} as { [name: string]: FlamingoTypes.ModeView })
);

export const flamingoColumns = createSelector(
  (state: AppState) => state.plan.columnsByRef,
  (columns) => columns
);

export const flamingoDataProfiling = createSelector(
  (state: AppState) => state.plan.dataProfiling,
  (dataProfiling) => dataProfiling
);

export const flamingoConditionalFormatting = createSelector(
  (state: AppState) => state.plan.conditionalFormatting,
  (conditionalFormatting) => conditionalFormatting
);

export const flamingoStates = createSelector(
  (state: AppState) => state.plan.states,
  (state: AppState) => state.flamingo.planRefs,
  ReportLayoutSelectors.chartTokensInLayout,
  (states, refs, chartTokens) =>
    chartTokens
      .filter((token) => states[refs[token]] != null)
      .reduce((data, token) => {
        data[token] = states[refs[token]];
        return data;
      }, {} as { [token: string]: FlamingoTypes.PlanStatus })
);

// GF version
export const flamingoStatesGf = createSelector(
  (state: AppState) => state.plan.states,
  (state: AppState) => state.flamingo.planRefs,
  fromReportViewerSelectors.chartTokensInLayoutGf,
  (states, refs, chartTokens) =>
    chartTokens
      .filter((token) => states[refs[token]] != null)
      .reduce((data, token) => {
        data[token] = states[refs[token]];
        return data;
      }, {} as { [token: string]: FlamingoTypes.PlanStatus })
);

export const flamingoErrors = createSelector(
  (state: AppState) => state.plan.errors,
  (state: AppState) => state.flamingo.planRefs,
  ReportLayoutSelectors.chartTokensInLayout,
  (states, refs, chartTokens) =>
    chartTokens
      .filter((token) => states[refs[token]] != null)
      .reduce((data, token) => {
        data[token] = states[refs[token]];
        return data;
      }, {} as { [token: string]: FlamingoResponse.FlamingoErrorResponse })
);

// GF version
export const flamingoErrorsGf = createSelector(
  (state: AppState) => state.plan.errors,
  (state: AppState) => state.flamingo.planRefs,
  fromReportViewerSelectors.chartTokensInLayoutGf,
  (states, refs, chartTokens) =>
    chartTokens
      .filter((token) => states[refs[token]] != null)
      .reduce((data, token) => {
        data[token] = states[refs[token]];
        return data;
      }, {} as { [token: string]: FlamingoResponse.FlamingoErrorResponse })
);

export const flamingo = createStructuredSelector({
  recordsByViz: flamingoResults,
  views: flamingoViews,
  pivotsByViz: pivotResultsGf,
  columnsByViz: flamingoColumns,
  statesByViz: flamingoStatesGf,
  errorsByViz: flamingoErrorsGf,
  dataProfiling: flamingoDataProfiling,
  conditionalFormatting: flamingoConditionalFormatting,
});

// need to use some legacy selectors because new state
// is not yet hydrated by the builder loading flow
export const flamingoForBuilder = createStructuredSelector({
  recordsByViz: flamingoResultsForBuilder,
  views: flamingoViews,
  pivotsByViz: pivotResults,
  columnsByViz: flamingoColumns,
  statesByViz: flamingoStates,
  errorsByViz: flamingoErrors,
  dataProfiling: flamingoDataProfiling,
  conditionalFormatting: flamingoConditionalFormatting,
});
